import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useSEO from '../hooks/useSEO';

const Login = () => {

  useSEO({
    title: 'Sign In to SERPfinity | Access Your SEO & AEO Dashboard',
    description: 'Sign in to your SERPfinity account to access AI-powered SEO and AEO tools. Track rankings, analyze SERPs, and optimize your content.',
    canonical: 'https://app.serpfinity.ai/login',
    robots: 'noindex, nofollow'
  });

  const navigate = useNavigate();
  const { login, loading } = useAuth();
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
        e.stopPropagation();
    setFormError('');
    const result = await login(formData.email, formData.password);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setFormError(result.message || result.error || 'Sign in failed. Check your email and password.');
    }
  };

  return (
    <>
      <Helmet>
        <title>Sign In to SERPfinity | Access Your SEO & AEO Dashboard</title>
        <meta name="description" content="Sign in to your SERPfinity account to access AI-powered SEO and AEO tools. Track rankings, analyze SERPs, monitor AI search visibility, and optimize your content." />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://app.serpfinity.ai/login" />
      </Helmet>
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h1 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h1>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link
              to="/register"
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              create a new account
            </Link>
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit} action="#" method="POST">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
                    autoComplete="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
              placeholder="Email address"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              required
                    autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
              placeholder="Password"
            />
          </div>

          <div>
            {formError && (
              <div role="alert" className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{formError}</div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-400"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>
              <div className="mt-4 text-center">
                <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-500">Forgot your password? Reset it here</Link>
              </div>
        </form>
      </div>
    </div>
    </>
  );
};

export default Login;